import React from "react";
import { Link } from "react-router-dom";
import { useDemoEmbedUrl } from "./DemoPage";
import { WhyFeedbackBox } from "./WhyCategoryPage";

/** One comparison row: what you can do, and who supports it. */
type Row = { need: string; babahanks: string; craigslist: string; ebay: string };

const ROWS: Row[] = [
    {
        need: "Search by what things actually are — exact features, yes/no options, choices, price ranges",
        babahanks: "✓ Every category has real filters",
        craigslist: "✗ Keyword text search only",
        ebay: "Partial — product filters vary by seller",
    },
    {
        need: "Find service providers by the area they SERVE (not where they happen to sit)",
        babahanks: "✓ Providers declare coverage; you search your town",
        craigslist: "✗ Posts live in city-wide buckets",
        ebay: "✗ No local services at all",
    },
    {
        need: "Find rentals and homes by their real location — state, county, or city",
        babahanks: "✓ Listings carry their location",
        craigslist: "Partial — one metro area per site",
        ebay: "✗",
    },
    {
        need: "Accept several options at once (\"condo OR town house\")",
        babahanks: "✓ Tick every choice you'd accept",
        craigslist: "✗",
        ebay: "Partial",
    },
    {
        need: "Products and local services in one search",
        babahanks: "✓",
        craigslist: "Partial — separate sections, same keyword search",
        ebay: "✗ Products only",
    },
    {
        need: "Keep selling where you already sell",
        babahanks: "✓ A listing can point at your eBay or Facebook page",
        craigslist: "—",
        ebay: "—",
    },
    {
        need: "Contact the seller directly — call, email, WhatsApp",
        babahanks: "✓ Right on the listing",
        craigslist: "Partial — anonymous email relay",
        ebay: "✗ Messages only, sale goes through eBay",
    },
];

const cell = (text: string): React.CSSProperties => ({
    padding: "0.6rem 0.75rem",
    borderTop: "1px solid var(--border-color, #e5e7eb)",
    verticalAlign: "top",
    color: text.startsWith("✓") ? "var(--success, #2e7d32)"
        : text.startsWith("✗") ? "var(--text-secondary, #6b7280)"
        : "inherit",
    fontWeight: text.startsWith("✓") ? 600 : 400,
});

/** Public /why page: what BabaHanks finds that keyword sites can't. */
const WhyPage: React.FC = () => {
    const demoAvailable = !!useDemoEmbedUrl();

    return (
        <div className="app-layout">
            <div className="public-topbar">
                <strong className="public-brand">BabaHanks</strong>
                <div className="public-links">
                    <Link to="/" className="btn-outline">Back to Search</Link>
                </div>
            </div>
            <div className="public-body">
                <div style={{ width: "100%", maxWidth: "960px", margin: "1rem auto", padding: "0 0.5rem" }}>
                    <h2 style={{ textAlign: "center" }}>Why BabaHanks?</h2>
                    <p style={{ textAlign: "center", color: "var(--text-secondary)", maxWidth: "640px", margin: "0.5rem auto 1.5rem" }}>
                        BabaHanks knows what things <em>are</em> — keyword sites only know
                        what words a seller typed. Search by real features, real coverage
                        areas, and real locations. And we don't handle the sale: we help
                        people find you, wherever you already sell.
                    </p>

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                            <thead>
                                <tr style={{ textAlign: "left" }}>
                                    <th style={{ padding: "0.6rem 0.75rem" }}>What you want to do</th>
                                    <th style={{ padding: "0.6rem 0.75rem" }}>BabaHanks</th>
                                    <th style={{ padding: "0.6rem 0.75rem" }}>Craigslist</th>
                                    <th style={{ padding: "0.6rem 0.75rem" }}>eBay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ROWS.map((r) => (
                                    <tr key={r.need}>
                                        <td style={{ ...cell(""), fontWeight: 500 }}>{r.need}</td>
                                        <td style={cell(r.babahanks)}>{r.babahanks}</td>
                                        <td style={cell(r.craigslist)}>{r.craigslist}</td>
                                        <td style={cell(r.ebay)}>{r.ebay}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: "0.75rem" }}>
                        For example: find a plumber who <em>covers your town</em>, installs
                        tankless water heaters, and offers 24-hour emergency call-outs — or
                        a condo <em>or</em> town house in your county, under your budget.
                        One search each.
                    </p>

                    <WhyFeedbackBox />

                    <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", margin: "1.5rem 0" }}>
                        <Link to="/" className="btn-outline">Try a search</Link>
                        {demoAvailable && <Link to="/demo" className="btn-outline">▶ Watch the demo</Link>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WhyPage;